/**
 * Error Handler Utility
 *
 * Centralized error normalization and handling logic
 */

import type { AppError, ApiError, NetworkError, ApplicationError, ErrorSeverity } from '../types/errors';

/**
 * Normalize any error to AppError format
 * This handles errors from various sources (axios, fetch, throw statements, etc.)
 */
export function normalizeError(error: unknown): AppError {
  // Handle API errors from httpClient (axios)
  if (isAxiosError(error)) {
    return {
      message: error.message,
      status: error.status,
      statusText: error.statusText,
      data: error.data,
      timestamp: new Date(),
    } as ApiError;
  }

  // Handle network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      message: 'Network error. Please check your connection.',
      offline: !navigator.onLine,
      retryable: true,
      timestamp: new Date(),
    } as NetworkError;
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return {
      message: error.message,
      recoverable: true,
      timestamp: new Date(),
    } as ApplicationError;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
      recoverable: true,
      timestamp: new Date(),
    } as ApplicationError;
  }

  // Handle unknown errors
  return {
    message: 'An unexpected error occurred',
    recoverable: true,
    timestamp: new Date(),
  } as ApplicationError;
}

/**
 * Extract user-friendly error message
 * This creates consistent error messages for end users
 */
export function getUserFriendlyMessage(error: AppError): string {
  // API errors - prefer backend message
  if ('status' in error) {
    if (error.data?.message) return error.data.message;
    if (error.message) return error.message;

    // Fallback to status-based messages
    switch (error.status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Authentication failed. Please login again.';
      case 403:
        return 'Access denied. You don\'t have permission for this action.';
      case 404:
        return 'Resource not found.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service unavailable. Please try again later.';
      default:
        return 'An error occurred. Please try again.';
    }
  }

  // Network errors
  if ('offline' in error) {
    return error.offline
      ? 'No internet connection. Please check your network.'
      : 'Network error. Please try again.';
  }

  // Return the error message as-is for other types
  return error.message;
}

/**
 * Determine error severity
 */
export function getErrorSeverity(error: AppError): ErrorSeverity {
  if ('status' in error) {
    if (error.status && error.status >= 500) return 'critical';
    if (error.status === 401 || error.status === 403) return 'error';
    if (error.status === 429) return 'warning';
    return 'error';
  }

  if ('offline' in error && error.offline) {
    return 'critical';
  }

  if ('recoverable' in error && !error.recoverable) {
    return 'critical';
  }

  return 'error';
}

/**
 * Check if error is from httpClient (axios-based)
 */
function isAxiosError(error: any): error is ApiError {
  return error && typeof error === 'object' && 'status' in error && 'message' in error;
}

/**
 * Log error to console (development) or tracking service (production)
 */
export function logError(error: AppError, context?: Record<string, any>): void {
  const severity = getErrorSeverity(error);
  const message = getUserFriendlyMessage(error);

  if (import.meta.env.DEV) {
    console.group(`[Error Handler] ${severity.toUpperCase()}`);
    console.error('Message:', message);
    console.error('Error:', error);
    if (context) console.error('Context:', context);
    console.groupEnd();
  } else {
    // In production, send to error tracking service (e.g., Sentry)
    // Example: Sentry.captureException(error, { extra: context });
  }
}

/**
 * Handle error with optional recovery action
 */
export function handleError(
  error: unknown,
  context?: Record<string, any>,
  onRecover?: () => void
): AppError {
  const normalizedError = normalizeError(error);
  logError(normalizedError, context);

  // If error is recoverable and recovery action is provided, execute it
  if ('recoverable' in normalizedError && normalizedError.recoverable && onRecover) {
    onRecover();
  }

  return normalizedError;
}
